import type { ApiClient } from '../../../utils/apiClient';

type Listener = () => void;

/**
 * The server stores only `https:` URLs; checked again here because the link is
 * shown on the sign-in page, before anyone is authenticated.
 */
const isHttps = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

/** `undefined` until the first read lands; `null` when no notice is set. */
let url: string | null | undefined;
let request: Promise<void> | null = null;
const listeners = new Set<Listener>();

const emit = () => listeners.forEach((listener) => listener());

/**
 * The privacy notice link, read once per page load and shared by every place
 * that shows it (the sidebar footer, the sign-in page, About), so a page
 * showing two of them makes one request. Saving the URL on the Privacy notice
 * tab writes here too, and every link updates without a reload.
 */
export function subscribePrivacyNotice(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const privacyNoticeSnapshot = (): string | null | undefined => url;

/**
 * Starts the read unless it has landed or is in flight. A failed read leaves
 * the link hidden and is tried again by the next page that shows it: the
 * sign-in page must never show an error for a missing notice.
 */
export function loadPrivacyNotice(client: ApiClient): void {
  if (url !== undefined || request) return;
  request = client
    .get<{ url: string | null }>('/api/privacy/notice')
    .then((body) => {
      url = isHttps(body?.url) ? body.url : null;
    })
    .catch(() => undefined)
    .finally(() => {
      request = null;
      emit();
    });
}

export function setPrivacyNoticeUrl(next: string | null): void {
  url = next;
  emit();
}

/** Tests only: forget the cached link between cases. */
export function resetPrivacyNoticeStore(): void {
  url = undefined;
  request = null;
}
